/**
 * HubSidebar — hierarchical navigation rail for the CategoricalExplainerHub.
 *
 * The 50+ explainer cards are grouped into 11 leaf sections; the sidebar
 * nests those 11 sections under three native `<details open>` super-groups
 * (Master theorems · Categorical structures · Structural Safety Mechanisms)
 * so a reader sees a real outline instead of a flat eleven-item list. The
 * disclosure widget is native HTML — no React state, no re-renders, screen
 * readers get correct semantics for free.
 *
 * Exported so per-section tests can mount `<HubSidebar /> + <SomeSection />`
 * without paying the cost of the full hub.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, PRIMARY } from "../../design-system/tokens.js";

export interface NavLinkSpec {
  readonly href: string;
  readonly label: string;
  readonly section:
    | "structure"
    | "adjunction"
    | "monad"
    | "operad"
    | "linear"
    | "master"
    | "nt"
    | "dpg"
    | "sdp"
    | "icg"
    | "ssm";
}

// inline: local
const NAV_LINKS: readonly NavLinkSpec[] = [
  {
    href: "#explainer-categorical-immunity",
    label: "Categorical Immunity Theorem",
    section: "master",
  },
  { href: "#explainer-purity", label: "Purity Invariant", section: "master" },
  { href: "#explainer-falgebra", label: "F-Algebra", section: "structure" },
  { href: "#explainer-fcoalgebra", label: "F-Coalgebra", section: "structure" },
  { href: "#explainer-recursion-schemes", label: "Recursion schemes", section: "structure" },
  { href: "#explainer-bisimulation", label: "Bisimulation", section: "structure" },
  { href: "#explainer-bialgebra", label: "Bialgebra", section: "structure" },
  { href: "#explainer-fibration", label: "Fibration / Opfibration", section: "structure" },
  { href: "#explainer-slice-category", label: "Slice / Coslice", section: "structure" },
  { href: "#explainer-double-category", label: "Double category", section: "structure" },
  { href: "#explainer-adjunction", label: "Adjunction", section: "adjunction" },
  { href: "#explainer-triangle-identities", label: "Triangle identities", section: "adjunction" },
  {
    href: "#explainer-adjunctions-generate-monads",
    label: "Adjunctions → monads",
    section: "adjunction",
  },
  { href: "#explainer-beck-chevalley", label: "Beck-Chevalley", section: "adjunction" },
  {
    href: "#explainer-higher-adjunctions",
    label: "Higher / graded adjunctions",
    section: "adjunction",
  },
  {
    href: "#explainer-scope-effect-coeffect",
    label: "Scope · Effect · Coeffect",
    section: "adjunction",
  },
  { href: "#explainer-monad", label: "Monad", section: "monad" },
  { href: "#explainer-comonad", label: "Comonad", section: "monad" },
  { href: "#explainer-graded-monad", label: "Graded monad", section: "monad" },
  { href: "#explainer-bimonad", label: "Bimonad", section: "monad" },
  { href: "#explainer-operad", label: "Operad (COMPOSE)", section: "operad" },
  { href: "#explainer-cooperad", label: "Cooperad (DECOMPOSE)", section: "operad" },
  { href: "#explainer-bioperad", label: "Bioperad (ICG-De)", section: "operad" },
  { href: "#explainer-linear-category", label: "Linear category", section: "linear" },
  { href: "#explainer-compact-closed", label: "Compact-closed", section: "linear" },
  { href: "#explainer-dagger-compact", label: "Dagger compact", section: "linear" },
  { href: "#explainer-data-origin", label: "DATA.ORIGIN (D1)", section: "nt" },
  { href: "#explainer-data-reach", label: "DATA.REACH (D2)", section: "nt" },
  { href: "#explainer-permit", label: "PERMIT (P)", section: "nt" },
  { href: "#explainer-separate", label: "SEPARATE (S)", section: "nt" },
  { href: "#explainer-precommit", label: "PRECOMMIT (W)", section: "nt" },
  { href: "#explainer-res-consume", label: "RES.CONSUME (R1)", section: "nt" },
  { href: "#explainer-res-release", label: "RES.RELEASE (R2)", section: "nt" },
  { href: "#explainer-compose", label: "COMPOSE (C)", section: "nt" },
  { href: "#explainer-data-lineage", label: "DATA.LINEAGE (D1+D2)", section: "dpg" },
  { href: "#explainer-res-balance", label: "RES.BALANCE (R1+R2)", section: "dpg" },
  { href: "#explainer-trace", label: "TRACE (Tr · L1)", section: "sdp" },
  { href: "#explainer-account", label: "ACCOUNT (St · L1)", section: "sdp" },
  { href: "#explainer-require", label: "REQUIRE (At · L2)", section: "sdp" },
  { href: "#explainer-delegate", label: "DELEGATE (Dt · L2)", section: "sdp" },
  { href: "#explainer-decompose", label: "DECOMPOSE (De · L2)", section: "sdp" },
  { href: "#explainer-partition", label: "PARTITION (Pa · L2)", section: "sdp" },
  { href: "#explainer-icg-tr", label: "ICG-Tr (DATA envelope)", section: "icg" },
  { href: "#explainer-icg-al", label: "ICG-Al (STATE envelope)", section: "icg" },
  { href: "#explainer-icg-st", label: "ICG-St (RESOURCE envelope)", section: "icg" },
  { href: "#explainer-icg-at", label: "ICG-At (TEMPORAL envelope)", section: "icg" },
  { href: "#explainer-icg-dt", label: "ICG-Dt (AUTHORITY envelope)", section: "icg" },
  { href: "#explainer-icg-de", label: "ICG-De (COMPOSITION envelope)", section: "icg" },
  { href: "#explainer-icg-pa", label: "ICG-Pa (DOMAIN envelope)", section: "icg" },
  { href: "#explainer-inv-establish", label: "INV.ESTABLISH (I1)", section: "ssm" },
  { href: "#explainer-inv-preserve", label: "INV.PRESERVE (I2)", section: "ssm" },
  { href: "#explainer-inv-consistency", label: "INV.CONSISTENCY (DPG)", section: "ssm" },
  { href: "#explainer-always", label: "ALWAYS (SDP)", section: "ssm" },
];

// Bucketed once at module load — NavGroup re-uses the slice on every render
// instead of filtering NAV_LINKS each time (the hub renders 11 NavGroups; the
// old `.filter` loop did 11×51=561 comparisons per render).
const NAV_LINKS_BY_SECTION: Readonly<Record<NavLinkSpec["section"], readonly NavLinkSpec[]>> =
  NAV_LINKS.reduce<Record<NavLinkSpec["section"], NavLinkSpec[]>>(
    (acc, link) => {
      acc[link.section].push(link);
      return acc;
    },
    {
      structure: [],
      adjunction: [],
      monad: [],
      operad: [],
      linear: [],
      master: [],
      nt: [],
      dpg: [],
      sdp: [],
      icg: [],
      ssm: [],
    },
  );

interface NavSuperGroupSpec {
  readonly title: string;
  readonly sections: readonly {
    readonly title: string;
    readonly section: NavLinkSpec["section"];
  }[];
}

const NAV_SUPERGROUPS: readonly NavSuperGroupSpec[] = [
  {
    title: "Master theorems",
    sections: [{ title: "Tier 1 (Immunity + Purity)", section: "master" }],
  },
  {
    title: "Categorical structures",
    sections: [
      { title: "Building blocks", section: "structure" },
      { title: "Adjunction family", section: "adjunction" },
      { title: "Monad family", section: "monad" },
      { title: "Operad family", section: "operad" },
      { title: "Linear / compact-closed family", section: "linear" },
    ],
  },
  {
    title: "Structural Safety Mechanisms",
    sections: [
      { title: "Neutralization Theorems (S1)", section: "nt" },
      { title: "Dual Pair Guarantees (S1→S1)", section: "dpg" },
      { title: "Compositionality Principles (S2)", section: "sdp" },
      { title: "Immunity Closure Guarantees (S1⊗S2)", section: "icg" },
      { title: "Pilot SSMs", section: "ssm" },
    ],
  },
];

export const HUB_ASIDE_STYLE: React.CSSProperties = {
  position: "sticky",
  top: "1rem",
  padding: "0.75rem",
  backgroundColor: "white",
  border: `1px solid ${GRAY[300]}`,
  borderRadius: "0.5rem",
  fontSize: "0.8125rem",
};

const NAV_GROUP_STYLE: React.CSSProperties = { marginBottom: "0.875rem" };

const NAV_GROUP_TITLE_STYLE: React.CSSProperties = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: GRAY[500],
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.375rem",
};

const NAV_GROUP_LIST_STYLE: React.CSSProperties = { margin: 0, padding: 0, listStyle: "none" };

const NAV_GROUP_ITEM_STYLE: React.CSSProperties = { margin: "0.125rem 0" };

const NAV_GROUP_LINK_STYLE: React.CSSProperties = {
  color: PRIMARY[700],
  textDecoration: "none",
  fontFamily: FONT.body,
};

const NAV_SUPERGROUP_STYLE: React.CSSProperties = {
  marginBottom: "0.5rem",
  borderRadius: "0.375rem",
};

const NAV_SUPERGROUP_SUMMARY_STYLE: React.CSSProperties = {
  cursor: "pointer",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: GRAY[900],
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  padding: "0.375rem 0.25rem",
  borderBottom: `1px solid ${GRAY[300]}`,
  userSelect: "none",
};

const NAV_SUPERGROUP_BODY_STYLE: React.CSSProperties = {
  paddingLeft: "0.25rem",
  paddingTop: "0.5rem",
};

export const HubSidebar = React.memo(function HubSidebar(): React.JSX.Element {
  return (
    <aside style={HUB_ASIDE_STYLE} aria-label="Explainer navigation">
      {NAV_SUPERGROUPS.map((group) => (
        <NavSuperGroup key={group.title} group={group} />
      ))}
    </aside>
  );
});

const NavSuperGroup = React.memo(function NavSuperGroup({
  group,
}: {
  readonly group: NavSuperGroupSpec;
}): React.JSX.Element {
  return (
    <details open style={NAV_SUPERGROUP_STYLE}>
      <summary style={NAV_SUPERGROUP_SUMMARY_STYLE}>{group.title}</summary>
      <div style={NAV_SUPERGROUP_BODY_STYLE}>
        {group.sections.map((entry) => (
          <NavGroup key={entry.section} title={entry.title} section={entry.section} />
        ))}
      </div>
    </details>
  );
});

const NavGroup = React.memo(function NavGroup({
  title,
  section,
}: {
  readonly title: string;
  readonly section: NavLinkSpec["section"];
}): React.JSX.Element {
  const items = NAV_LINKS_BY_SECTION[section];
  return (
    <nav style={NAV_GROUP_STYLE} aria-label={title}>
      <div style={NAV_GROUP_TITLE_STYLE}>{title}</div>
      <ul style={NAV_GROUP_LIST_STYLE}>
        {items.map((item) => (
          <li key={item.href} style={NAV_GROUP_ITEM_STYLE}>
            <a href={item.href} style={NAV_GROUP_LINK_STYLE}>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
});
